import {DownloadIcon} from '@heroicons/react/outline';
import {useState} from 'react';

import {buildDownloadFileName} from '@afriex/core/utils';

import {Button} from '@/components';
import {merchantService} from '@/services/merchants';
import type {MarketPlaceTradeQuery} from '@/types/MarketPlace';
import {saveBlob} from '@/utils/downloadFile';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

type DownloadTradesCsvButtonProps = {
  /** The filters the list on screen was fetched with, not the staged panel. */
  filters: MarketPlaceTradeQuery;
};

/**
 * Exports every trade matching the applied filters. The server streams the
 * CSV and returns 429 while another export is running; both messages are
 * surfaced verbatim.
 */
const DownloadTradesCsvButton = ({filters}: DownloadTradesCsvButtonProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await merchantService.downloadMarketPlaceTradesCsv(filters);
      // Scoped by the offer when the export covers only one.
      saveBlob(
        blob,
        buildDownloadFileName(
          'marketplace_trades',
          'csv',
          filters.askId ? String(filters.askId) : undefined,
        ),
      );
    } catch (error) {
      showErrorMessage(
        getErrorMessage(error, 'Failed to export marketplace trades'),
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      colorScheme="cyan"
      variant="outline"
      disabled={isDownloading}
      isLoading={isDownloading}
      onClick={() => {
        handleDownload().catch(
          logRejection('Failed to export marketplace trades'),
        );
      }}
      testId="download-marketplace-trades-button"
      aria-label="Export trades to CSV"
      title="Export trades to CSV"
    >
      <DownloadIcon className="h-5 w-5" />
    </Button>
  );
};

export default DownloadTradesCsvButton;
